// deps
import { ReactNode } from "react";

// interfaces
import { ON_SUCCESS_CALLBACK_TYPE, TICK_ACCENT, TICK_STATES } from "../constants";
import { DeliveryAddress } from "@swiggy-private/payment-types";

export interface TickScreenA11yTexts {
    [TICK_STATES.LOADING]: string;
    [TICK_STATES.SUCCESS]: string;
}

export interface SuperMessage {
    successMessage?: string;
    successMessageInfo?: string;
}

/** Lottie text layer name (`nm`) mapped to the text value to apply on that layer. */
export type TransitionLottieLayerText = Record<string, string>;

/** Custom transition lottie overrides (e.g. Toing Quick). */
export interface TransitionLottieConfig {
    url: string;
    layerText?: TransitionLottieLayerText;
    animationSegment?: number[];
    loadingSegment?: number[];
    successSegment?: number;
    delay?: number;
}

export interface TickScreenProps {
    tickState: TICK_STATES;
    postSuccessCb?: VoidFunction;
    isSuper?: boolean;
    isSwiggyMoney?: boolean;
    isAutoPay?: boolean;
    mainText?: string;
    subText?: string;
    subTextNodeList?: ReactNode[];
    intermediateText?: string;
    customSuccessScreen?: boolean;
    a11yTexts?: TickScreenA11yTexts;
    accent?: TICK_ACCENT;
    showBottomAnimation?: boolean;
    cardBrand?: string;
    tagElement?: ReactNode;
    isRegular?: boolean;
    deliveryAddress?: DeliveryAddress;
    successTimeout?: number;
    getSuperMessage?: () => SuperMessage | undefined;
    isOfferBundleApplied?: boolean;
    isMilestoneUnlockEligible?: boolean;
    onTransitionScreenError?: OnTransitionError;
    transitionScreenEnabled?: boolean;
    onTransitionScreenView?: VoidFunction;
    recordTransitionScreenLoadTime?: (loadTime: number, domLoadedTime?: number, imageLoadedTime?: number) => void;
    isSwiggyBlackApplicable?: boolean;
    isSwiggyBlackOtpAnimationApplicable?: boolean;
    isPopCart?: boolean;
    successFooterMessage?: {
        text: string;
        icon?: string;
    };
    isBoltCartM3?: boolean;
    isAffordableCartM2?: boolean;
    isGiftingCart?: boolean;
    isJioHotstarFlow?: boolean;
    isStealStoreCart?: boolean;
    transitionLottieConfig?: TransitionLottieConfig;
    tickScreenLottieUrl?: string;
    maxLinesForDeliveryAddress?: number;
    showDeliveryAddress?: boolean;
    isMroExperienceEnabledOnOrderConfirmationScreen?: boolean;
    childOrdersCount?: number;
}

export interface TransitionPlayer {
    tickState: TICK_STATES;
    onDOMLoaded?: VoidFunction;
    onError?: OnTransitionError;
    deliveryAddressAnotation?: string;
    recordTransitionScreenLoadTime?: (loadTime: number, domLoadedTime?: number, imageLoadedTime?: number) => void;
    isSwiggyBlackApplicable?: boolean;
    isSwiggyBlackOtpAnimationApplicable?: boolean;
    isPopCart?: boolean;
    isBoltCartM3?: boolean;
    isAffordableCartM2?: boolean;
    isGiftingCart?: boolean;
    transitionLottieConfig?: TransitionLottieConfig;
    tickScreenLottieUrl?: string;
    onSuccessState?: VoidFunction;
    isMroFlow?: boolean;
    childOrdersCount?: number;
}

export interface ITransitionScreen extends TickScreenProps {
    onError?: OnTransitionError;
}

export interface SavingIconProps {
    width?: number;
    height?: number;
    isFreeDel?: boolean;
    isToingFlow?: boolean;
}

export interface SavingWrapperProps {
    isFreeDel?: boolean;
    isToingFlow?: boolean;
}

export interface SavingTextProps {
    tickState: TICK_STATES;
    savings?: SuperMessage;
    isFreeDel?: boolean;
    isMilestoneUnlockEligible?: boolean;
    isToingFlow?: boolean;
}

export enum TRANSITION_ERROR_TYPES {
    FETCH_FAILED = "FETCH_FAILED",
    TIMEOUT = "TIMEOUT",
    LOTTIE_ERROR = "LOTTIE_ERROR",
    ASSET_FETCH_ERROR = "ASSET_FETCH_ERROR",
    UNKNOWN = "UNKNOWN",
}

export type OnTransitionError = (errorType?: TRANSITION_ERROR_TYPES, error?: unknown, url?: string) => void;

export interface FetchTransitionLottie {
    onError: OnTransitionError;
    deliveryAddressAnotation?: string;
    isSwiggyBlackApplicable?: boolean;
    isSwiggyBlackOtpAnimationApplicable?: boolean;
    isPopCart?: boolean;
    isBoltCartM3?: boolean;
    isAffordableCartM2?: boolean;
    isGiftingCart?: boolean;
    transitionLottieConfig?: TransitionLottieConfig;
    tickScreenLottieUrl?: string;
    isMroFlow?: boolean;
    childOrdersCount?: number;
}

export interface GetTickScreenLottie {
    isSwiggyBlackApplicable?: boolean;
    isSwiggyBlackOtpAnimationApplicable?: boolean;
    isPopCart?: boolean;
    isBoltCartM3?: boolean;
    isAffordableCartM2?: boolean;
    isGiftingCart?: boolean;
    isMroFlow?: boolean;
}

export interface TickScreenTransitionLottieConfig {
    lottieUrl: string | undefined;
    onSuccessCallbackType: ON_SUCCESS_CALLBACK_TYPE;
}

// [[ START - lottie-web types ]]
// adding custom lottie-web types since lottie-web
// doesn't define types for these
export interface EnterFrameEvent {
    currentTime: number;
}

export interface LottieMarker {
    cm: string; // Marker name
    tm: number; // Frame number
}
export interface PostSuccessDelayOptions {
    isSwiggyBlackApplicable?: boolean;
    blackBenefitsTimeout: number;
    successTimeout: number;
    isBoltCartM3?: boolean;
    isAffordableCartM2?: boolean;
    isToingFlow?: boolean;
    isGiftingCart?: boolean;
    transitionLottieConfig?: TransitionLottieConfig;
    isMroFlow?: boolean;
}
// [[ END - lottie-web types ]]
